import {
  ActionType,
  Action,
  Beneficiary,
  Pharmacy,
  LowIncomeSubsidyStatus,
  Plan,
  CoverageInfo,
  OtherBenefit,
  OtherBenefitCategory,
  UserLanguage,
  PacePlan,
  BasePlan,
  ExtendedCoverageInfo,
  OecStatus,
  PlanType,
  PlanBenefitRedactionIssue,
  RedactionType,
  DrugPricingRedactionIssue,
  Redaction,
  SearchResultPlan,
  CoverageType,
  AdditionalSupplementalBenefits,
  OtherRedactionIssue,
  RolloverPlanInfo,
  PlanIdParts,
} from "../@types";
import { getPlanCard, getPlanInfoForNextYear } from "../api";
import { Dispatch } from "react";
import { isMaPlanType } from "./planTypes";
import {
  DrugPricingRedactionIssues,
  PlanBenefitRedactionIssues,
} from "./CONSTANTS";
import { ApiError, logError } from "./errors";
import {
  parsePlanLongIdIntoUrlPartForApi,
  makePlanLongId,
} from "./objectUtilities";

export const fetchCurrentPlan = async ({
  beneficiary,
  dispatch,
  futureLis,
  isOutsideOpenEnrollment,
  lis,
  pharmacies = [],
}: {
  beneficiary: Beneficiary | undefined;
  dispatch: Dispatch<Action>;
  futureLis?: LowIncomeSubsidyStatus;
  isOutsideOpenEnrollment?: boolean;
  lis: LowIncomeSubsidyStatus;
  pharmacies?: Pharmacy[];
}): Promise<void> => {
  if (!beneficiary || !beneficiary.coverage_current.length) {
    return Promise.resolve();
  }

  const coverage = {
    ...beneficiary.coverage_current[0],
    contract_year: beneficiary.coverage_current[0].coverage_year,
  };

  if (
    !coverage.contract_id ||
    !coverage.contract_year ||
    !coverage.plan_id ||
    !coverage.segment_id
  ) {
    return Promise.resolve();
  }

  const apiUrl = parsePlanLongIdIntoUrlPartForApi(makePlanLongId(coverage));
  const npis = pharmacies.map(p => p.npi);

  if (!isOutsideOpenEnrollment) {
    const rolloverPlanInfo = await fetchPlanIdsForNextYear(coverage);

    if (rolloverPlanInfo && rolloverPlanInfo[0]) {
      if (rolloverPlanInfo.length === 1) {
        const longIdForNextYear = parsePlanLongIdIntoUrlPartForApi(
          makePlanLongId(rolloverPlanInfo[0])
        );
        getPlanCard(longIdForNextYear, npis, futureLis || lis)
          .then(plan => {
            dispatch({
              type: ActionType.ADD_NEXT_YEAR_COVERAGE,
              payload: plan,
            });

            dispatch({
              type: ActionType.UPDATE_ROLLOVER_PLAN_STATUS,
              payload: rolloverPlanInfo[0].status,
            });
          })
          .catch(e => {
            logError(
              "Failed to retrieve plan card for next year plan (planHelpers/fetchCurrentPlan)",
              e
            );
          });
      } else if (rolloverPlanInfo.length > 1) {
        dispatch({
          type: ActionType.UPDATE_MULTIPLE_ROLLOVER_PLANS,
          payload: true,
        });
      }
    }
  }

  return getPlanCard(apiUrl, npis, lis)
    .then(plan => {
      dispatch({
        type: ActionType.ADD_CURRENT_COVERAGE,
        payload: plan,
      });
    })
    .catch(e => {
      logError(
        "Failed to retrieve plan card (planHelpers/fetchCurrentPlan)",
        e
      );
    });
};

export const shouldSuppressPlanBenefits = (redactions: Redaction[]): boolean =>
  redactions.some(
    redaction =>
      redaction.type === RedactionType.suppression &&
      PlanBenefitRedactionIssues.includes(
        redaction.issue as PlanBenefitRedactionIssue
      )
  );

/**
 * Used for calculating the portion of an MSA plan's deductible for which a bene
 * is responsible.
 *
 * As of OE 2024, the `annual_deductible` property for an MSA plan is a string
 * that has text like "$9,400 annual deductible"
 *
 * Other plans may have very different strings, even containing both in-network
 * and out-of-network deductible values, but MSAs have no concept of OON & IN
 *
 * The assumption about what this string will be might be subject to breakage,
 * but for now, works
 */
export const calculateYourMSAResponsibility = (
  healthDeductible: number | string,
  msaYearlyDeposit: number
): number => {
  let deductible = healthDeductible;

  if (typeof deductible === "string") {
    deductible = deductible.replace(/\D/g, "");
  }

  const result = Number(deductible) - msaYearlyDeposit;

  return result < 0 ? 0 : result;
};

export const shouldSuppressDrugPricing = (redactions: Redaction[]): boolean =>
  redactions.some(
    redaction =>
      redaction.type === RedactionType.suppression &&
      DrugPricingRedactionIssues.includes(
        redaction.issue as DrugPricingRedactionIssue
      )
  );

export const fetchPlanIdsForNextYear = async (
  coverage: CoverageInfo
): Promise<RolloverPlanInfo[] | undefined> => {
  const parsedCoverage = {
    ...coverage,
    contract_year: coverage.coverage_year,
  };

  if (
    !parsedCoverage.contract_id ||
    !parsedCoverage.contract_year ||
    !parsedCoverage.plan_id ||
    !parsedCoverage.segment_id
  ) {
    return undefined;
  }

  try {
    const nextYearPlanIds = await getPlanInfoForNextYear(
      parsePlanLongIdIntoUrlPartForApi(makePlanLongId(parsedCoverage))
    );
    return nextYearPlanIds;
  } catch (e) {
    logError("Could not get plan info for next year", e as ApiError);
    return undefined;
  }
};

export const getAdditionalBenefit = (
  plan: Plan,
  type: keyof AdditionalSupplementalBenefits,
  category: string,
  benefit: string
): OtherBenefit | null => {
  const cat = `SB_CAT_${category.toUpperCase()}`;
  const bene = `SB_${benefit.toUpperCase()}`;

  if (plan.additional_supplemental_benefits) {
    const otherCategory = plan.additional_supplemental_benefits[type].find(
      (benefit: OtherBenefitCategory) => benefit.category === cat
    );

    if (otherCategory) {
      const otherBenefit = otherCategory.benefits.find(
        (benefit: OtherBenefit) => benefit.benefit === bene
      );

      if (otherBenefit) {
        return otherBenefit;
      }
    }
  }

  return null;
};

export const planHasDrugCoverage = (plan: Plan | SearchResultPlan): boolean =>
  !isMaPlanType(plan.plan_type);

export const getPlanName = (
  plan: BasePlan | PacePlan | ExtendedCoverageInfo | OecStatus,
  language: UserLanguage
): string => {
  let planNameToReturn = "name" in plan ? plan.name : plan.plan_name;

  if ("name" in plan) {
    if (language === UserLanguage.ENGLISH) {
      planNameToReturn = plan.name;
    } else {
      planNameToReturn = plan.name_esp ? plan.name_esp : plan.name;
    }
  } else {
    if (language === UserLanguage.ENGLISH) {
      planNameToReturn = plan.plan_name;
    } else {
      planNameToReturn = plan.plan_name_esp
        ? plan.plan_name_esp
        : plan.plan_name;
    }
  }

  return planNameToReturn || "";
};

export const getPlanTypeForCoverageType = (
  coverageType: CoverageType
): string =>
  coverageType === CoverageType.MAPD ? PlanType.MAPD : PlanType.PDP;

export const isValidLongPlanId = (id?: string): boolean =>
  !id ? false : /\d{4}-[A-Z|0-9]+-\d+-\d+/.test(id);

/** @example "2021/H5652/002/0" */
export const isValidApiLongPlanId = (id?: string): boolean =>
  !id ? false : /\d{4}\/[A-Z|0-9]+\/\d+\/\d+/.test(id);

/**
 * Returns the plan year according to whichever property contains it, or an empty string
 */
export const getPlanYearToUse = (
  plan: OecStatus | SearchResultPlan | ExtendedCoverageInfo
) => {
  let yearToUse = "";
  if ("year" in plan) {
    yearToUse = plan.year;
  } else if ("coverage_year" in plan) {
    yearToUse = plan.coverage_year;
  } else if ("contract_year" in plan && plan.contract_year) {
    yearToUse = plan.contract_year;
  }
  return yearToUse;
};

/**
 * Asserts whether a plan's contract year is GT or LT a given test year
 * @param params
 * @param params.contractYear - A `plan.contract_year` string
 * @param params.testYear - A numeric year value
 * @param [params.test] - Can be set to `"greaterThan"` or `"lessThan"`
 */
export const contractYearTest = ({
  contractYear,
  testYear,
  test,
}: {
  contractYear: string;
  testYear: number;
  test: "greaterThan" | "lessThan";
}): boolean => {
  const contractYearNum = Number(contractYear);
  if (isNaN(contractYearNum)) return false;
  return test === "greaterThan"
    ? contractYearNum > testYear
    : contractYearNum < testYear;
};

export const contractYearLessThan = (contractYear: string, testYear: number) =>
  contractYearTest({ contractYear, testYear, test: "lessThan" });

export const contractYearGreaterThan = (
  contractYear: string,
  testYear: number
) => contractYearTest({ contractYear, testYear, test: "greaterThan" });

// @TODO: rename this to account for coverage being current or future
// @see https://jira.cms.gov/browse/MCT-10651
export const isCurrentPlan = (
  coverageInfo: CoverageInfo | undefined,
  plan: PlanIdParts
): boolean => {
  if (!coverageInfo) return false;

  return (
    coverageInfo.plan_id === plan.plan_id &&
    coverageInfo.segment_id === plan.segment_id &&
    coverageInfo.contract_id === plan.contract_id &&
    (coverageInfo.contract_year === plan.contract_year ||
      coverageInfo.coverage_year === plan.contract_year)
  );
};

export const getCantEnrollPlan = ({
  coverage,
  isCsr,
  plan: { plan_id, contract_id, contract_year, segment_id },
}: {
  coverage: SearchResultPlan | undefined;
  isCsr: boolean;
  plan: Plan | SearchResultPlan;
}) =>
  !isCsr &&
  isCurrentPlan(coverage, {
    plan_id,
    contract_id,
    contract_year,
    segment_id,
  });

export const getPlanHasDsnpSuppression = ({
  contract_year,
  redactions,
}: Plan | SearchResultPlan) =>
  +contract_year > 2024 &&
  redactions.some(
    r =>
      r.type === RedactionType.suppression &&
      r.issue === OtherRedactionIssue.d_snp
  );

export const getPlanIsFrozen = ({ redactions }: Plan | SearchResultPlan) =>
  redactions.some(
    r =>
      r.type === RedactionType.frozen &&
      r.issue === OtherRedactionIssue.frzn_enrlmnt
  );
